// A2F-801: eCTD Document Formatting pack rule definitions.
//
// Seven ScanRules covering body font discipline, image accessibility,
// and submission cleanliness — the subset of eCTD Backbone File
// Specification v3.2.2 Appendix 7 (PDF Specification) that the current
// Pro executor can express with existing primitives (paragraphs /
// tables / images, font.* / paragraph.* / text.* / image.* readables).
//
// Six additional planned rules (black-body-text, paragraph shading,
// section margins, page size, hyperlink validation, header/footer
// page numbering) deferred to A2F-801-followup: each needs either a
// WordApi version bump (paragraph.shading from 1.6), executor
// expansion (run-level iteration for color/links, sections target
// for page setup, header/footer iteration), or OOXML parsing
// (section margins). Filed together since they share one
// "executor capability spike" gate.
//
// Body-text scoping: rules that should only apply to body paragraphs
// (not headings) filter on `paragraph.outlineLevel greaterThan 9`.
// Word's outline level convention is 1-9 for Heading 1-9 and 10 for
// Body Text / Normal. MANUAL-VERIFY: confirm Word.Paragraph.outlineLevel
// returns 10 (not 0 or undefined) for unstyled body paragraphs in the
// installed Word build.
//
// Same own-file structure as ind-pack-rules.ts for clean git blame;
// rules are spread into ALL_RULES in tag-rules.ts so the prebuild
// validator and ALL_RULES_BY_ID lookup resolve them like any other
// rule. Pack-only — not added to TAG_RULES; discoverability is via
// the eCTD pack toggle in /configure and My Chrona.

import type { ScanRule } from '../types'

// eCTD Appendix 7 §2: body text in Times New Roman 9-12pt.
// Body-scoped via outlineLevel AND a Caption-style exclusion;
// Appendix 7 permits sans-serif for tables and figure/table captions,
// and captions inherit outline level 10 from their underlying
// non-heading paragraph, so an outlineLevel-only filter would
// false-positive on every caption. The substring exclusion catches
// the default Word styles ("Caption", "Figure Caption", "Table
// Caption") and most reasonable custom variants.
// Tables themselves don't appear in body.paragraphs (table-cell
// paragraphs live under table.rows[].cells[].body.paragraphs), so
// they're auto-excluded by the 'paragraphs' target alone.
export const ectd_body_font_times_new_roman: ScanRule = {
  id: 'ectd_body_font_times_new_roman',
  type: 'scan',
  tier: 'pro',
  privacy: 'local',
  label: 'Body font: Times New Roman',
  icon: 'type',
  description:
    'Body paragraphs should use Times New Roman per eCTD Backbone v3.2.2 Appendix 7 §2.',
  targets: ['paragraphs'],
  conditions: [
    { property: 'paragraph.outlineLevel', operator: 'greaterThan', value: 9 },
    { property: 'paragraph.style', operator: 'notContains', value: 'Caption' },
    { property: 'font.name', operator: 'notEquals', value: 'Times New Roman' },
  ],
  conditionLogic: 'all',
  action: 'flag',
  severity: 'warn',
  message:
    'Body paragraph uses a non-serif font. eCTD Appendix 7 specifies Times New Roman for narrative body text.',
}

// eCTD Appendix 7 §2: body text size 9-12pt (12pt preferred for
// narrative). Split into lower- and upper-bound rules because the
// executor's condition language has no nested disjunction —
// "(body) AND (size < 9 OR size > 12)" can't be expressed in a
// single ScanRule, and merging the two violations into one rule
// with conditionLogic 'any' would also match every heading.
export const ectd_body_font_size_min: ScanRule = {
  id: 'ectd_body_font_size_min',
  type: 'scan',
  tier: 'pro',
  privacy: 'local',
  label: 'Body font ≥9pt',
  icon: 'type',
  description:
    'Body paragraphs should be at least 9pt per eCTD Backbone v3.2.2 Appendix 7 §2.',
  targets: ['paragraphs'],
  conditions: [
    { property: 'paragraph.outlineLevel', operator: 'greaterThan', value: 9 },
    { property: 'paragraph.style', operator: 'notContains', value: 'Caption' },
    { property: 'font.size', operator: 'lessThan', value: 9 },
  ],
  conditionLogic: 'all',
  action: 'flag',
  severity: 'warn',
  message:
    'Body paragraph font size is below 9pt. eCTD Appendix 7 specifies 9-12pt for narrative.',
}

export const ectd_body_font_size_max: ScanRule = {
  id: 'ectd_body_font_size_max',
  type: 'scan',
  tier: 'pro',
  privacy: 'local',
  label: 'Body font ≤12pt',
  icon: 'type',
  description:
    'Body paragraphs should not exceed 12pt per eCTD Backbone v3.2.2 Appendix 7 §2.',
  targets: ['paragraphs'],
  conditions: [
    { property: 'paragraph.outlineLevel', operator: 'greaterThan', value: 9 },
    { property: 'paragraph.style', operator: 'notContains', value: 'Caption' },
    { property: 'font.size', operator: 'greaterThan', value: 12 },
  ],
  conditionLogic: 'all',
  action: 'flag',
  severity: 'warn',
  message:
    'Body paragraph font size exceeds 12pt. eCTD Appendix 7 specifies 9-12pt for narrative.',
}

// Section 508 accessibility + eCTD §3: every image needs alt text.
// Distinct from scan_missing_alt_text (which ships as a tag rule at
// 'warn'); this pack-only variant is 'fail' severity because eCTD
// submissions are rejected for accessibility violations.
export const ectd_image_alt_text_required: ScanRule = {
  id: 'ectd_image_alt_text_required',
  type: 'scan',
  tier: 'pro',
  privacy: 'local',
  label: 'Image alt text required',
  icon: 'alert',
  description:
    'Images must have alt text for Section 508 / eCTD accessibility compliance.',
  targets: ['images'],
  conditions: [{ property: 'image.altText', operator: 'isEmpty' }],
  conditionLogic: 'any',
  action: 'flag',
  severity: 'fail',
  message:
    'Image missing alt text. Section 508 and eCTD accessibility require alt text on every image.',
}

// eCTD Appendix 7 §5/§7: images must fit within the letter page
// minus 1" left + 0.75" right margins, i.e. ≤ 6.75" usable width.
// Word.js image.width is in points; 6.75in × 72 pt/in = 486 pt.
// We flag at 500pt to allow a small tolerance for figure captions
// that pad slightly past the standard margin.
// MANUAL-VERIFY: confirm Word.InlinePicture.width unit is points (not
// pixels or twips) in the installed Word build.
export const ectd_image_width_within_page: ScanRule = {
  id: 'ectd_image_width_within_page',
  type: 'scan',
  tier: 'pro',
  privacy: 'local',
  label: 'Image fits within page',
  icon: 'alert',
  description:
    'Images should fit within the printable page width (~6.75" for letter with 1.25" margins).',
  targets: ['images'],
  conditions: [{ property: 'image.width', operator: 'greaterThan', value: 500 }],
  conditionLogic: 'any',
  action: 'flag',
  severity: 'warn',
  message:
    'Image width exceeds the eCTD printable page area. Resize to ≤ 6.75" so it fits within standard letter margins.',
}

// Submission cleanliness: strikethrough is for tracked-changes
// review, not final submission. Catches paragraphs left in
// "review" formatting that should have been accepted or rejected
// before the document was sequenced.
export const ectd_no_strikethrough_text: ScanRule = {
  id: 'ectd_no_strikethrough_text',
  type: 'scan',
  tier: 'pro',
  privacy: 'local',
  label: 'No strikethrough text',
  icon: 'flag',
  description:
    'Strikethrough is review-only formatting; finalize tracked changes before submission.',
  targets: ['paragraphs'],
  conditions: [{ property: 'font.strikeThrough', operator: 'equals', value: true }],
  conditionLogic: 'any',
  action: 'flag',
  severity: 'warn',
  message:
    'Paragraph has strikethrough formatting. Accept/reject the tracked change before submitting.',
}

// Submission cleanliness: hidden text can leak content into the
// PDF render and is rejected on review. Distinct from
// scan_hidden_text — same font.hidden condition, but this eCTD-pack
// variant is 'fail' because hidden text in a submission is a
// structural defect, not a stylistic nit. A2F-850.7 aligned both
// rules on font.hidden alone.
export const ectd_no_hidden_text: ScanRule = {
  id: 'ectd_no_hidden_text',
  type: 'scan',
  tier: 'pro',
  privacy: 'local',
  label: 'No hidden text',
  icon: 'flag',
  // A2F-850.7: description clarifies the rule's scope so reviewers
  // don't expect detection of white-on-white or other visual
  // concealment — those need a separate ticket.
  description:
    "Checks Word's Hidden Text formatting flag (Format > Font > Hidden). Does not detect white-on-white or other visual concealment. Hidden text can leak into the rendered PDF; remove before eCTD submission.",
  targets: ['paragraphs'],
  conditions: [{ property: 'font.hidden', operator: 'equals', value: true }],
  conditionLogic: 'any',
  action: 'flag',
  severity: 'fail',
  message:
    'Paragraph contains hidden text. Remove hidden runs before sequencing the submission.',
}

export const ECTD_PACK_RULES = [
  ectd_body_font_times_new_roman,
  ectd_body_font_size_min,
  ectd_body_font_size_max,
  ectd_image_alt_text_required,
  ectd_image_width_within_page,
  ectd_no_strikethrough_text,
  ectd_no_hidden_text,
] as const
